import { addEvent, removeEvent, triggerEvent, Throttle } from '../utils/events';
import { addClasses, wrap, unwrap, smoothScroll } from '../utils/html';

class Tab {
  /**
   * @param  {Tabs}     tabs        Instance of Tabs which contains this tab
   * @param  {Dom}      toggle      toggle button
   * @param  {Dom}      tab         block to hide or show
   * @param  {Dom}      toggleGroup group of toggles
   * @param  {Object}   config      Config object defining whether the tab should be
   *                                scrollable and/or closeable.
   */
  constructor(tabs, toggle, tab, toggleGroup, config = {}) {
    const { scrollable = true, closeable = false } = config;
    this.tabs = tabs;
    this.toggle = toggle;
    this.tab = tab;
    this.toggleGroup = toggleGroup;
    this.scrollable = scrollable;
    this.closeable = closeable;
    this.scrollableOn = false;
    if (this.toggle.classList.contains(this.tabs.activeToggleClassName)) {
      this.open();
    } else {
      this.close();
    }
    this.init();
  }

  init() {
    addEvent(this.toggle, 'click', this.open.bind(this));
    // Check if the tab is closeable
    if (this.closeable) {
      // Wrap the tab title in a <span> so that we can inject the close button before it
      const tabTitle = document.createElement('span');
      tabTitle.innerText = this.toggle.innerText;
      // Add the close icon and bind the click event
      this.btnClose = document.createElement('button');
      addClasses(this.btnClose, [ 'btn', 'btn--transparent', 'hard', 'push-quarter--right' ]);
      this.btnClose.innerHTML = '<i class="fa fa-times"></i>';
      this.toggle.innerText = '';
      this.toggle.appendChild(this.btnClose);
      this.toggle.appendChild(tabTitle);
      addEvent(this.btnClose, 'click', this.remove.bind(this));
    }
  }

  open() {
    if (this.tabs.active === this) {
      // already open
      return;
    }
    if (this.tabs.active) {
      this.tabs.active.close();
    }
    this.tabs.active = this;
    this.tab.style.display = 'block';
    this.toggle.classList.add(this.tabs.activeToggleClassName);
    if (this.scrollable) {
      const tabToggleWidth = this.toggle.clientWidth;
      const tabToggleLeft = this.toggle.offsetLeft;
      const tabToggleRight = tabToggleLeft + tabToggleWidth;
      const tabToggleGroupWidth = this.toggleGroup.clientWidth;
      if (tabToggleLeft < this.toggleGroup.scrollLeft) {
        this.tabs.scroll(tabToggleLeft);
      }
      if (tabToggleRight > (this.toggleGroup.scrollLeft + tabToggleGroupWidth)) {
        this.tabs.scroll(tabToggleRight - tabToggleGroupWidth);
      }
    }
    triggerEvent(this.toggle, 'open.commonui.tabs');
  }

  close() {
    this.tab.style.display = 'none';
    this.toggle.classList.remove(this.tabs.activeToggleClassName);
  }

  remove(event) {
    if (event) {
      event.stopImmediatePropagation();
    }
    const tabIndex = this.tabs.initedTabs.indexOf(this);
    this.tabs.remove({ index: tabIndex });
    removeEvent(this.btnClose, 'click', this.remove);
  }

  destroy() {
    removeEvent(this.toggle, 'click', this.open);
  }
}

export class Tabs {
  constructor(container, config = {}) {
    const { responsive, scrollable, vertical } = config;
    this.container = container;
    this.responsive = responsive;

    if (vertical && scrollable) {
      /* eslint-disable no-console */
      console.warn('vertical tabs cannot be scrollable');
      /* eslint-enable no-console */
    }

    this.vertical = vertical;
    this.scrollable = this.vertical === true ? false : scrollable;
    this.setClassNames();

    this.setDropdown = this.setDropdown.bind(this);
    this.dropdownUpdate = this.dropdownUpdate.bind(this);

    this.magicNumberForTabToggleOffset = 6;
    this.magicNumberForResizeThrottling = 100;

    this.init();
  }

  init() {
    this.toggleGroup = this.container.querySelector(this.toggleGroupSelector);
    this.tabsGroup = this.container.querySelector(this.tabsGroupSelector);
    this.toggleDOMElements = this.container.querySelectorAll(this.toggleSelector);
    this.tabDOMElements = this.container.querySelectorAll(this.tabSelector);
    this.toggles = Array.from(this.toggleDOMElements);
    this.tabs = Array.from(this.tabDOMElements);

    this.initedTabs = [];

    if (!this.isEverythingOk()) {
      return;
    }

    for (let index = 0; index < this.toggles.length; index++) {
      const toggleDOMElement = this.toggles[index];
      const tabDOMElement = this.tabs[index];
      const isCloseable = toggleDOMElement.classList.contains('is-closeable');
      const tabOptions = { scrollable: this.scrollable, closeable: isCloseable };
      const tab = new Tab(this, toggleDOMElement, tabDOMElement, this.toggleGroup, tabOptions);
      this.initedTabs.push(tab);
    }

    if (this.vertical) {
      this.container.classList.add('tabs--vertical');
    }

    if (this.responsive) {
      this.container.classList.add('tabs--responsive');
      this.createResponsiveDropdown();
    }

    this.setScrollableState();

    this.windowScrollResize = new Throttle(this.setScrollableState.bind(this), this.magicNumberForResizeThrottling);
    addEvent(window, 'resize', this.windowScrollResize.trigger);
  }

  setScrollableState() {
    if (!this.scrollable) {
      return;
    }
    if (this.checkScrollableState() && !this.scrollableOn) {
      wrap(this.toggleGroup, 'tabs--scrollable');

      this.scrollLeftButton = document.createElement('button');
      this.scrollRightButton = document.createElement('button');

      addClasses(this.scrollLeftButton, [ 'tabs__scroller', 'tabs__scroller--left', 'js-action--scroll-left' ]);
      addClasses(this.scrollRightButton, [ 'tabs__scroller', 'tabs__scroller--right', 'js-action--scroll-right' ]);

      this.scrollLeftButton.innerHTML = '<i class="fa fa-chevron-left"></i>';
      this.scrollRightButton.innerHTML = '<i class="fa fa-chevron-right"></i>';

      this.toggleGroup.parentNode.insertBefore(this.scrollLeftButton, this.toggleGroup);
      this.toggleGroup.parentNode.appendChild(this.scrollRightButton);

      // Initialize some instance variables
      this.scrollLeftPosition = 0;
      this.scrollIndex = 0;

      this.checkScrollButtonState();
      this.windowResize = new Throttle(this.checkScrollButtonState.bind(this), this.magicNumberForResizeThrottling);

      addEvent(this.scrollLeftButton, 'click', this.scrollLeft.bind(this));
      addEvent(this.scrollRightButton, 'click', this.scrollRight.bind(this));
      addEvent(window, 'resize', this.windowResize.trigger);

      this.scrollableOn = true;
    } else if (!this.checkScrollableState() && this.scrollableOn) {
      if (this.scrollLeftButton) {
        this.scrollLeftButton.remove();
      }
      if (this.scrollRightButton) {
        this.scrollRightButton.remove();
      }
      unwrap(this.toggleGroup, 'tabs--scrollable');
      removeEvent(window, 'resize', this.windowResize.trigger);
      this.scrollableOn = false;
    } else if (this.checkScrollableState() && this.scrollableOn) {
      this.checkScrollButtonState();
    }
  }

  checkScrollableState() {
    return this.toggleGroup.scrollWidth > this.toggleGroup.clientWidth;
  }
  /**
   * Scrolls the tab toggle group to the previous tab, i.e. to the left.
   */
  scrollLeft() {
    // Check that there is room to scroll,
    // i.e. if we're already on the first tab then don't change the scroll index
    if (this.scrollIndex > 0) {
      this.scrollIndex--;
    }
    this.scrollLeftPosition	-= this.toggles[this.scrollIndex].clientWidth + this.magicNumberForTabToggleOffset;
    if (this.scrollLeftPosition < 0) {
      this.scrollLeftPosition = 0;
    }
    this.scroll(this.scrollLeftPosition);
  }

  /**
   * Scrolls the tab toggle group to the next tab, i.e. to the right.
   */
  scrollRight() {
    this.scrollLeftPosition += this.toggles[this.scrollIndex].clientWidth + this.magicNumberForTabToggleOffset;
    // Check that there is still room to scroll,
    // i.e. if we're already on the last tab then don't change the scroll index
    if (this.scrollIndex < this.toggles.length - 1) {
      this.scrollIndex++;
    }
    this.scroll(this.scrollLeftPosition);
  }

  /**
   * Scrolls the tab toggle group to the specified position and then updates the scrolling button state.
   * @param position
   */
  scroll(position) {
    smoothScroll(this.toggleGroup, { to: position, cbk: this.checkScrollButtonState.bind(this) });
  }

  /**
   * Sets the 'disabled' state of each of the left and right scrolling buttons
   * based on the scroll position of the overflowed container.
   */
  checkScrollButtonState() {
    if (this.toggleGroup.scrollLeft <= 0) {
      this.scrollLeftButton.setAttribute('disabled', true);
    } else {
      this.scrollLeftButton.removeAttribute('disabled');
    }
    if (this.toggleGroup.scrollLeft + this.toggleGroup.clientWidth >= this.toggleGroup.scrollWidth) {
      this.scrollRightButton.setAttribute('disabled', true);
    } else {
      this.scrollRightButton.removeAttribute('disabled');
    }
  }

  open(options = {}) {
    const { index, selector = '' } = options;
    if (index >= 0 && index < this.initedTabs.length) {
      this.initedTabs[index].open();
      return;
    }
    if (selector === '') {
      return;
    }
    const tabToFind = document.querySelector(selector);
    if (tabToFind) {
      /* eslint-disable arrow-parens */
      const foundTab = this.initedTabs.find(x => x.tab === tabToFind);
      /* eslint-enable arrow-parens */
      if (foundTab) {
        foundTab.open();
        return;
      }
    }
  }

  close(options = {}) {
    const { index, selector = '' } = options;
    if (index >= 0 && index < this.initedTabs.length) {
      this.initedTabs[index].close();
      return;
    }
    if (selector === '') {
      return;
    }
    const tabToFind = document.querySelector(selector);
    if (tabToFind) {
      /* eslint-disable arrow-parens */
      const foundTab = this.initedTabs.find(x => x.tab === tabToFind);
      /* eslint-enable arrow-parens */
      if (foundTab) {
        foundTab.close();
        return;
      }
    }
  }

  add(config = {}) {
    let { index } = config;
    const { title = '', scrollable = true } = config;
    const newTabToggle = document.createElement('div');
    const newTab = document.createElement('div');
    const newTabTitle = document.createElement('span');
    newTabTitle.innerText = title;
    newTabToggle.classList.add(this.toggleClassName);
    newTabToggle.appendChild(newTabTitle);

    newTab.classList.add(this.tabClassName);
    newTab.style.display = 'none';

    index = index || this.initedTabs.length;

    const tab = new Tab(this, newTabToggle, newTab, this.toggleGroup, config);
    if (index >= 0 && index < this.initedTabs.length) {
      this.toggleGroup.insertBefore(newTabToggle, this.toggleGroup.children[index]);
      this.tabsGroup.insertBefore(newTab, this.tabsGroup.children[index]);
      this.toggles.splice(index, 0, newTabToggle);
      this.initedTabs.splice(index, 0, tab);
    } else {
      this.toggleGroup.appendChild(newTabToggle);
      this.tabsGroup.appendChild(newTab);
      this.toggles.push(newTabToggle);
      this.initedTabs.push(tab);
    }

    // Add the new tab to the dropdown if in responsive mode
    if (this.responsive) {
      this.addDropdownItem(index, title, title);
    }

    if (scrollable) {
      this.setScrollableState();
    }

    // Open the tab and autoscroll to it
    tab.open();

    // Finally, return the tab
    return tab;
  }

  remove(options = {}) {
    const tabIndex = options.index;
    // Ensure that the index of the tab we are going to remove is valid
    if (tabIndex < 0 || tabIndex >= this.initedTabs.length) {
      return;
    }
    const tabToggle = this.toggleGroup.children[tabIndex];
    const tab = this.tabsGroup.children[tabIndex];
    this.toggleGroup.removeChild(tabToggle);
    this.tabsGroup.removeChild(tab);
    if (this.responsive) {
      removeEvent(this.initedTabs[tabIndex].toggle, 'open.commonui.tabs', this.setDropdown);
      this.dropdown.removeChild(this.dropdown.children[tabIndex]);
    }
    this.toggles.splice(tabIndex, 1);
    this.initedTabs.splice(tabIndex, 1);
    this.setScrollableState();
    // If the tab being removed is the active tab then
    // open the tab after this tab, as long as it wasn't the last tab
    if (tabToggle.classList.contains(this.activeToggleClassName)) {
      let tabIndexToOpen = tabIndex;
      if (tabIndexToOpen >= this.initedTabs.length) {
        tabIndexToOpen = this.initedTabs.length - 1;
      }
      this.open({ index: tabIndexToOpen });
    }
  }

  createResponsiveDropdown() {
    this.dropdown = document.createElement('select');
    this.dropdown.classList.add('visuallyhidden--xs-and-up');
    for (let index = 0; index < this.initedTabs.length; index++) {
      // Create select option for each tab
      this.addDropdownItem(index, this.toggles[index].textContent, this.toggles[index].textContent);
    }
    addEvent(this.dropdown, 'change', this.dropdownUpdate);
    this.container.insertBefore(this.dropdown, this.container.firstChild);
  }

  /**
   * Adds an item to the responsive dropdown at the specified index, with the specified value and text.
   * @param {Number} index  The index that the dropdown item should be injected at.
   * @param {String} value  The value set on the dropdown item.
   * @param {String} text   The text to be displayed in the dropdown item.
   */
  addDropdownItem(index, value, text) {
    const option = document.createElement('option');
    option.value = value;
    option.text = text;
    if (index < this.dropdown.children.length - 1) {
      this.dropdown.insertBefore(option, this.dropdown.children[index]);
    } else {
      this.dropdown.appendChild(option);
    }
    // Add class to hide on xs on the buttons
    this.toggles[index].classList.add('visuallyhidden--xs-only');
    // Bind to each open to keep dropdown in sync
    addEvent(this.initedTabs[index].toggle, 'open.commonui.tabs', this.setDropdown);
  }

  setDropdown(event) {
    this.dropdown.value = event.srcElement.textContent;
  }

  dropdownUpdate(event) {
    this.initedTabs[event.target.selectedIndex].open();
  }

  /**
   * Initializes classes and selectors for blocks
   */
  setClassNames() {
    this.blockClassName = 'tabs';
    this.toggleGroupSelector = '.tabs__toggle-group';
    this.tabsGroupSelector = '.tabs__tabs-group';
    this.toggleClassName = 'tabs__toggle';
    this.toggleSelector = `.${ this.toggleClassName }`;
    this.tabClassName = 'tabs__tab';
    this.tabSelector = `.${ this.tabClassName }`;
    this.activeToggleClassName = 'tabs__toggle--active';
  }

  /* eslint-disable no-console */
  isEverythingOk() {
    if (this.toggles.length === 0) {
      console.warn('CommonUI - Tabs - There\'s no toggles for tabs');
      return false;
    } else if (this.tabs.length === 0) {
      console.warn('CommonUI - Tabs - There\'s no content tabs');
      return false;
    } else if (this.toggles.length !== this.tabs.length) {
      console.warn('CommonUI - Tabs - Tabs toggles and tabs amounts are not matching');
      return false;
    }
    return true;
  }
  /* eslint-enable no-console */

  destroy() {
    if (this.responsive) {
      for (let index = 0; index < this.initedTabs.length; index++) {
        removeEvent(this.initedTabs[index].toggle, 'open.commonui.tabs', this.setDropdown);
        removeEvent(this.dropdown, 'change', this.dropdownUpdate);
      }
      this.dropdown.outerHTML = '';
    }
    if (this.scrollable) {
      removeEvent(this.scrollLeftButton, 'click', this.scrollLeft.bind(this));
      removeEvent(this.scrollRightButton, 'click', this.scrollRight.bind(this));
      removeEvent(window, 'resize', this.windowResize.trigger);
    }
    while (this.initedTabs.length > 0) {
      const tab = this.initedTabs.pop();
      tab.destroy();
    }
  }
}

/**
 * iterates through all matched blocks and initializes tabs classes
 * @param  {Object} config
 */
export default function initTabs(config = { selector: '.js-tabs', responsive: false, scrollable: true }) {
  let tabs = null;
  if (typeof config.selector === 'string') {
    for (const container of document.querySelectorAll(config.selector)) {
      tabs = new Tabs(container, config);
    }
  }
  return tabs;
}



// WEBPACK FOOTER //
// ./js/components/Tabs.js